import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Activity,
  AlertCircle,
  Briefcase,
  Coins,
  FileText,
  Key,
  Loader2,
  RefreshCw,
  Sparkles,
  Target,
} from "lucide-react";

import {
  formatAppStatus,
  formatCreditReason,
} from "@/components/admin/admin-user-detail-modal";
import { client } from "@/utils/orpc";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@super-resume-new/ui/components/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@super-resume-new/ui/components/table";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin/tools")({
  head: () =>
    seo({
      title: "Tools & Features Analytics | JobCues Admin",
      description: "Aggregate usage statistics for Resume Tailor, ATS Stealth Booster, Fit Matcher, and Developer API.",
      path: "/admin/tools",
      noindex: true,
    }),
  component: AdminToolsPage,
});

function AdminToolsPage() {
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ["admin", "tools-usage"],
    queryFn: () => client.admin.getToolsUsage(),
    staleTime: 60_000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Activity className="size-5 text-brand" />
            Tools &amp; Feature Usage Analytics
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Real-time activity and credit volume across AI Resume Tailor, ATS Stealth Booster, Fit Matcher, and Developer API.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="h-8 gap-1.5 text-xs w-fit"
        >
          <RefreshCw className={`size-3.5 ${isRefetching ? "animate-spin" : ""}`} />
          Refresh Metrics
        </Button>
      </div>

      {isLoading ? (
        <Card className="border-border p-12 text-center text-muted-foreground">
          <Loader2 className="mx-auto size-8 animate-spin text-brand mb-2" />
          <p className="text-sm font-medium">Aggregating platform tool statistics…</p>
        </Card>
      ) : isError || !data ? (
        <Card className="border-destructive/30 p-8 text-center text-destructive">
          <AlertCircle className="mx-auto size-8 mb-2" />
          <p className="text-sm font-semibold">Failed to aggregate tool metrics</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-3">
            Retry
          </Button>
        </Card>
      ) : (
        <>
          {/* Top 6 KPI Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Card 1: AI Resume Tailor */}
            <Card className="border-border bg-card shadow-xs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  AI Resume Tailor
                </CardTitle>
                <div className="flex size-7 items-center justify-center rounded-lg bg-brand/10 text-brand-foreground border border-brand/20">
                  <Sparkles className="size-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums">
                  {data.tools.resumeTailor.totalGenerations}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{data.tools.resumeTailor.uniqueUsers}</span> unique candidates ·{" "}
                  <span className="font-semibold text-amber-500">{data.tools.resumeTailor.creditsConsumed} cr</span> debited
                </p>
              </CardContent>
            </Card>

            {/* Card 2: ATS Stealth Booster */}
            <Card className="border-border bg-card shadow-xs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  ATS Stealth Booster
                </CardTitle>
                <div className="flex size-7 items-center justify-center rounded-lg bg-violet-500/10 text-violet-500 border border-violet-500/20">
                  <FileText className="size-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums text-violet-500">
                  {data.tools.atsBooster.totalGenerations}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{data.tools.atsBooster.uniqueUsers}</span> candidates ·{" "}
                  <span className="font-semibold text-amber-500">{data.tools.atsBooster.creditsConsumed} cr</span> debited
                </p>
              </CardContent>
            </Card>

            {/* Card 3: Instant Fit Matcher */}
            <Card className="border-border bg-card shadow-xs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Instant Fit Matcher
                </CardTitle>
                <div className="flex size-7 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                  <Target className="size-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums text-emerald-500">
                  {data.tools.matchChecker.totalMatches}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Avg fit score: <span className="font-semibold text-foreground">{data.tools.matchChecker.avgScore}%</span> ·{" "}
                  <span className="text-emerald-500 font-semibold">{data.tools.matchChecker.strongCount} strong</span> /{" "}
                  <span className="text-amber-500 font-semibold">{data.tools.matchChecker.possibleCount} mid</span>
                </p>
              </CardContent>
            </Card>

            {/* Card 4: Job Applications Tracker */}
            <Card className="border-border bg-card shadow-xs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Job Applications Tracker
                </CardTitle>
                <div className="flex size-7 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500 border border-blue-500/20">
                  <Briefcase className="size-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums">
                  {data.tools.jobTracker.totalApplications}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{data.tools.jobTracker.uniqueUsers}</span> active job seekers tracking jobs
                </p>
              </CardContent>
            </Card>

            {/* Card 5: Developer API Platform */}
            <Card className="border-border bg-card shadow-xs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Developer Platform &amp; API
                </CardTitle>
                <div className="flex size-7 items-center justify-center rounded-lg bg-muted text-foreground border border-border">
                  <Key className="size-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums">
                  {data.tools.developerApi.totalRequests}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{data.tools.developerApi.totalKeys} keys</span> ({data.tools.developerApi.activeKeys} active) across{" "}
                  <span className="font-semibold text-foreground">{data.tools.developerApi.uniqueDevelopers}</span> devs
                </p>
              </CardContent>
            </Card>

            {/* Card 6: Credit Economy Summary */}
            <Card className="border-border bg-card shadow-xs">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Credit Economy Volume
                </CardTitle>
                <div className="flex size-7 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500 border border-amber-500/20">
                  <Coins className="size-4" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold tabular-nums text-amber-500">
                  {data.creditEconomy.circulating} cr
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-emerald-500">+{data.creditEconomy.totalMinted}</span> minted ·{" "}
                  <span className="font-semibold text-destructive">-{data.creditEconomy.totalBurned}</span> burned
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Application Pipeline Status Breakdown */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Briefcase className="size-4 text-brand" />
                Job Applications Pipeline Funnel
              </CardTitle>
              <CardDescription className="text-xs">
                Distribution of candidate job statuses in the built-in Kanban / tracker.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {data.tools.jobTracker.statusBreakdown.map((sb) => {
                  const statusInfo = formatAppStatus(sb.status);
                  return (
                    <div key={sb.status} className="rounded-lg border border-border p-3 bg-muted/20 flex flex-col justify-between">
                      <span className="text-[11px] font-semibold text-muted-foreground uppercase">
                        {statusInfo.label}
                      </span>
                      <div className="text-xl font-bold text-foreground mt-2 tabular-nums">
                        {sb.count}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Credit Economy Reasons Breakdown */}
          <Card className="border-border bg-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Coins className="size-4 text-amber-500" />
                Credit Inflow &amp; Outflow by Ledger Reason
              </CardTitle>
              <CardDescription className="text-xs">
                Audited transaction categories representing signups, purchases, AI tool debits, and grants.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category / Reason</TableHead>
                    <TableHead>Transactions</TableHead>
                    <TableHead>Net Volume</TableHead>
                    <TableHead className="text-right">Unique Users</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.creditEconomy.reasons.map((r) => (
                    <TableRow key={r.reason}>
                      <TableCell className="font-semibold text-xs text-foreground">
                        {formatCreditReason(r.reason)}
                        <span className="block text-[10px] font-mono text-muted-foreground">{r.reason}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs tabular-nums">{r.count}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            r.totalDelta > 0
                              ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 font-mono font-semibold"
                              : "bg-destructive/10 text-destructive border-destructive/30 font-mono font-semibold"
                          }
                        >
                          {r.totalDelta > 0 ? `+${r.totalDelta}` : r.totalDelta} cr
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs text-muted-foreground tabular-nums">
                        {r.uniqueUsers} accounts
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Side-by-side Recent Operations Feeds */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Recent AI Resume Tailor Generations */}
            <Card className="border-border bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="size-4 text-brand" />
                  Recent AI Resume Tailors
                </CardTitle>
                <CardDescription className="text-xs">
                  Latest resume optimizations generated by users.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {data.tools.resumeTailor.recent.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Target Job</TableHead>
                        <TableHead>Candidate</TableHead>
                        <TableHead className="text-right">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.tools.resumeTailor.recent.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs">
                            <div className="flex flex-col">
                              <span className="font-semibold text-foreground truncate max-w-[200px]">
                                {r.jobTitle}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {r.company || "Unknown Company"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="flex flex-col">
                              <span className="font-medium text-foreground truncate max-w-[160px]">
                                {r.userName || "User"}
                              </span>
                              <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[160px]">
                                {r.userEmail}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                            {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="p-6 text-xs text-muted-foreground text-center">No resume tailors recorded yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Right: Recent Instant Fit Checks */}
            <Card className="border-border bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Target className="size-4 text-emerald-500" />
                  Recent Fit Match Checks
                </CardTitle>
                <CardDescription className="text-xs">
                  Latest match tests between resumes and job postings.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {data.tools.matchChecker.recent.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job Posting</TableHead>
                        <TableHead>Fit Score</TableHead>
                        <TableHead className="text-right">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.tools.matchChecker.recent.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell className="text-xs">
                            <div className="flex flex-col">
                              <span className="font-semibold text-foreground truncate max-w-[200px]">
                                {m.jobTitle || "Custom Job Posting"}
                              </span>
                              <span className="text-[11px] text-muted-foreground">
                                {m.jobCompany || (m.userEmail ? m.userEmail : "Guest Visitor")}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <Badge
                                variant="outline"
                                className={
                                  m.score >= 75
                                    ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30 font-mono font-semibold"
                                    : m.score >= 50
                                      ? "bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono font-semibold"
                                      : "bg-destructive/10 text-destructive border-destructive/30 font-mono font-semibold"
                                }
                              >
                                {Math.round(m.score)}%
                              </Badge>
                              <span className="text-[10px] uppercase font-semibold text-muted-foreground">
                                {m.band}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                            {m.createdAt ? new Date(m.createdAt).toLocaleDateString() : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="p-6 text-xs text-muted-foreground text-center">No match checks recorded yet.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
